// src/pages/nutrition.tsx
'use client';
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getSupabase, currentUser, signOut } from "@/lib/supabase";
import { Edit3, X, Check } from 'lucide-react';
import { useModals } from "@/context/ModalContext";
import Sidebar from "@/components/Sidebar";
import {
  Home, Apple, Dumbbell, Trophy, Settings, LogOut,
  Calendar, ChevronDown, ChevronUp, Trash2, CalendarDays, Utensils,
  TrendingUp, Target, Award, Flame, Loader2
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';

// ——————————————————————————————————
// TYPES
// ——————————————————————————————————
interface MacroTargets {
  cal: number;
  pro: number;
  carb: number;
  fat: number;
}

interface ChartDataPoint {
  month?: string;
  day?: string | number;
  cal: number | null;
  target: number;
}

interface MealLogEntry {
  id: string;
  created_at: string;
  meals: any[];
  totals: { calories: number; protein: number; carbs: number; fat: number };
  user_message: string | null;
  meal_plan_text: string | null;
}

interface GroupedMealLog {
  date: string;
  logs: MealLogEntry[];
}

// ——————————————————————————————————
// HELPER FUNCTIONS
// ——————————————————————————————————

function toDateKey(dateInput: string | Date): string {
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  return date.toISOString().split('T')[0];
}

function calculateStreak(logs: Array<{ created_date: string }>): number {
  if (!logs || logs.length === 0) return 0;

  const uniqueDates = new Set(logs.map(l => toDateKey(l.created_date)));
  
  let streak = 0;
  const checkDate = new Date();
  checkDate.setHours(0, 0, 0, 0);

  for (let i = 0; i < 365; i++) {
    const dateKey = toDateKey(checkDate);
    
    if (uniqueDates.has(dateKey)) {
      streak++;
    } else if (i > 0) {
      break;
    }
    
    checkDate.setDate(checkDate.getDate() - 1);
  }

  return streak;
}

function calculateConsistency(logs: Array<{ created_date: string }>): number {
  if (!logs || logs.length === 0) return 0;

  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  thirtyDaysAgo.setHours(0, 0, 0, 0);

  const uniqueDates = new Set<string>();
  
  logs.forEach(l => {
    const logDate = new Date(l.created_date);
    if (logDate >= thirtyDaysAgo) {
      uniqueDates.add(toDateKey(l.created_date));
    }
  });

  return Math.round((uniqueDates.size / 30) * 100);
}

function groupLogsByDay(logs: Array<{
  created_date: string;
  daily_total_calories: number | null;
  daily_total_protein_grams: number | null;
  daily_total_carb_grams: number | null;
  daily_total_fat_grams: number | null;
}>): Record<string, MacroTargets & { count: number }> {
  const daily: Record<string, MacroTargets & { count: number }> = {};
  
  logs.forEach(log => {
    const key = toDateKey(log.created_date);
    
    if (!daily[key]) {
      daily[key] = { cal: 0, pro: 0, carb: 0, fat: 0, count: 0 };
    }
    
    daily[key].cal += log.daily_total_calories || 0;
    daily[key].pro += log.daily_total_protein_grams || 0;
    daily[key].carb += log.daily_total_carb_grams || 0;
    daily[key].fat += log.daily_total_fat_grams || 0;
    daily[key].count += 1;
  });

  return daily;
}

// ——————————————————————————————————
// MAIN NUTRITION PAGE
// ——————————————————————————————————
export default function Nutrition() {
  const { openAddActivity, openLogFood, openUpdateGoal } = useModals();
  
  const [isLoading, setIsLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('12months');
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
  const [avgMacros, setAvgMacros] = useState<MacroTargets>({ cal: 0, pro: 0, carb: 0, fat: 0 });
  const [targets, setTargets] = useState<MacroTargets>({ cal: 2769, pro: 150, carb: 300, fat: 80 });
  const [dietStyle, setDietStyle] = useState('Mediterranean');
  const [intolerances, setIntolerances] = useState('None');
  const [mealLogs, setMealLogs] = useState<GroupedMealLog[]>([]);
  
  const [editingDiet, setEditingDiet] = useState(false);
  const [editingIntolerances, setEditingIntolerances] = useState(false);
  const [tempDiet, setTempDiet] = useState(dietStyle);
  const [tempIntolerances, setTempIntolerances] = useState(intolerances);
  const [expandedDays, setExpandedDays] = useState<Record<string, boolean>>({});

  const [streak, setStreak] = useState(0);
  const [periodAvg, setPeriodAvg] = useState(0);
  const [consistency, setConsistency] = useState(0);

  const toggleDay = (date: string) => {
    setExpandedDays(prev => ({ ...prev, [date]: !prev[date] }));
  };

  const refreshAll = async () => {
    const supabase = getSupabase();
    const user = await currentUser();
    
    if (!user) {
      console.warn("[nutrition] No authenticated user");
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    try {
      let startDate: Date | null = null;
      
      if (timeRange === '12months') {
        startDate = new Date();
        startDate.setFullYear(startDate.getFullYear() - 1);
      } else if (timeRange === '30days') {
        startDate = new Date();
        startDate.setDate(startDate.getDate() - 30);
      } else if (timeRange === '7days') {
        startDate = new Date();
        startDate.setDate(startDate.getDate() - 6);
      }

      let query = supabase
        .from("daily_calorie_macro_logs")
        .select("created_date, daily_total_calories, daily_total_protein_grams, daily_total_carb_grams, daily_total_fat_grams")
        .eq("user_id", user.id);

      if (startDate) {
        query = query.gte("created_date", startDate.toISOString().split('T')[0]);
      }

      const { data: logs, error: logError } = await query.order("created_date", { ascending: true });

      if (logError) {
        console.error("[nutrition] Error fetching macro logs:", logError.message);
        throw logError;
      }

      const allLogs = logs || [];

      setStreak(calculateStreak(allLogs));
      setConsistency(calculateConsistency(allLogs));

      const { data: goalData, error: goalError } = await supabase
        .from("client_goal_logs")
        .select("suggested_daily_calories, suggested_daily_protein, suggested_daily_carbs, suggested_daily_fat")
        .eq("user_id", user.id)
        .order("created_date", { ascending: false })
        .limit(1);

      if (goalError) {
        console.error("[nutrition] Error fetching goals:", goalError.message);
      }

      const goal = goalData?.[0] || {
        suggested_daily_calories: 2769,
        suggested_daily_protein: 150,
        suggested_daily_carbs: 300,
        suggested_daily_fat: 80,
      };

      setTargets({
        cal: goal.suggested_daily_calories,
        pro: goal.suggested_daily_protein,
        carb: goal.suggested_daily_carbs,
        fat: goal.suggested_daily_fat,
      });

      const { data: profileData, error: profileError } = await supabase
        .from("clients")
        .select("diet_style, food_intolerences")
        .eq("user_id", user.id)
        .single();

      if (profileError) {
        console.error("[nutrition] Error fetching profile:", profileError.message);
      } else if (profileData) {
        setDietStyle(profileData.diet_style || 'Mediterranean');
        setIntolerances(profileData.food_intolerences || 'None');
        setTempDiet(profileData.diet_style || 'Mediterranean');
        setTempIntolerances(profileData.food_intolerences || 'None');
      }

      const daily = groupLogsByDay(allLogs);
      const now = new Date();
      let newChartData: ChartDataPoint[] = [];
      let newAvgMacros: MacroTargets = { cal: 0, pro: 0, carb: 0, fat: 0 };

      if (timeRange === '12months') {
        const monthlyTotals: Record<string, { cal: number; pro: number; carb: number; fat: number; days: number }> = {};
        
        Object.entries(daily).forEach(([dateStr, data]) => {
          const date = new Date(dateStr);
          const key = `${date.getFullYear()}-${String(date.getMonth()).padStart(2, '0')}`;
          
          if (!monthlyTotals[key]) {
            monthlyTotals[key] = { cal: 0, pro: 0, carb: 0, fat: 0, days: 0 };
          }
          
          monthlyTotals[key].cal += data.cal;
          monthlyTotals[key].pro += data.pro;
          monthlyTotals[key].carb += data.carb;
          monthlyTotals[key].fat += data.fat;
          monthlyTotals[key].days += 1;
        });

        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        
        for (let i = 11; i >= 0; i--) {
          const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
          const key = `${date.getFullYear()}-${String(date.getMonth()).padStart(2, '0')}`;
          const monthData = monthlyTotals[key];
          
          newChartData.push({
            month: months[date.getMonth()],
            cal: monthData ? Math.round(monthData.cal / monthData.days) : null,
            target: goal.suggested_daily_calories,
          });
        }

        const currentKey = `${now.getFullYear()}-${String(now.getMonth()).padStart(2, '0')}`;
        const current = monthlyTotals[currentKey];
        
        if (current && current.days > 0) {
          newAvgMacros = {
            cal: Math.round(current.cal / current.days),
            pro: Math.round(current.pro / current.days),
            carb: Math.round(current.carb / current.days),
            fat: Math.round(current.fat / current.days),
          };
          setPeriodAvg(Math.round(current.cal / current.days));
        }
      } else {
        const daysBack = timeRange === '30days' ? 29 : 6;
        const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

        for (let i = daysBack; i >= 0; i--) {
          const date = new Date();
          date.setDate(date.getDate() - i);
          const dateKey = toDateKey(date);
          const dayData = daily[dateKey];

          newChartData.push({
            day: timeRange === '7days' ? dayNames[date.getDay()] : date.getDate(),
            cal: dayData ? dayData.cal : null,
            target: goal.suggested_daily_calories,
          });
        }

        const daysWithData = Object.keys(daily).length;
        
        if (daysWithData > 0) {
          const totals = Object.values(daily).reduce(
            (acc, d) => ({
              cal: acc.cal + d.cal,
              pro: acc.pro + d.pro,
              carb: acc.carb + d.carb,
              fat: acc.fat + d.fat,
            }),
            { cal: 0, pro: 0, carb: 0, fat: 0 }
          );

          newAvgMacros = {
            cal: Math.round(totals.cal / daysWithData),
            pro: Math.round(totals.pro / daysWithData),
            carb: Math.round(totals.carb / daysWithData),
            fat: Math.round(totals.fat / daysWithData),
          };
          setPeriodAvg(Math.round(totals.cal / daysWithData));
        }
      }

      setChartData(newChartData);
      setAvgMacros(newAvgMacros);

      const { data: mealData, error: mealError } = await supabase
        .from("meal_plan_log")
        .select("id, created_at, meals, totals, user_message, meal_plan_text")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (mealError) {
        console.error("[nutrition] Meal log error:", mealError.message);
        setMealLogs([]);
      } else {
        const groupedMeals = (mealData || []).reduce((acc: Record<string, MealLogEntry[]>, log: any) => {
          const date = new Date(log.created_at).toLocaleDateString('en-GB');
          
          if (!acc[date]) acc[date] = [];

          let parsedMeals: any[] = [];
          try {
            parsedMeals = typeof log.meals === 'string' 
              ? JSON.parse(log.meals) 
              : (Array.isArray(log.meals) ? log.meals : []);
          } catch (e) {
            console.warn("[nutrition] Failed to parse meals:", log.meals);
          }

          let parsedTotals = { calories: 0, protein: 0, carbs: 0, fat: 0 };
          try {
            parsedTotals = typeof log.totals === 'string'
              ? JSON.parse(log.totals)
              : (typeof log.totals === 'object' && log.totals !== null ? log.totals : parsedTotals);
          } catch (e) {
            console.warn("[nutrition] Failed to parse totals:", log.totals);
          }

          acc[date].push({
            ...log,
            meals: parsedMeals,
            totals: parsedTotals,
          });

          return acc;
        }, {});

        setMealLogs(
          Object.entries(groupedMeals).map(([date, logs]) => ({ date, logs }))
        );
      }
    } catch (err: any) {
      console.error("[nutrition] Refresh error:", err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    refreshAll();
  }, [timeRange]);

  const deleteMealLog = async (id: string) => {
    const { error } = await getSupabase().from("meal_plan_log").delete().eq("id", id);
    
    if (error) {
      console.error("[nutrition] Error deleting meal log:", error.message);
      return;
    }
    
    refreshAll();
  };

  const saveDietStyle = async () => {
    const supabase = getSupabase();
    const user = await currentUser();
    if (!user) return;

    const { error } = await supabase
      .from("clients")
      .update({ diet_style: tempDiet })
      .eq("user_id", user.id);

    if (error) {
      console.error("[nutrition] Error saving diet style:", error.message);
      return;
    }

    setDietStyle(tempDiet);
    setEditingDiet(false);
  };

  const saveIntolerances = async () => {
    const supabase = getSupabase();
    const user = await currentUser();
    if (!user) return;

    const { error } = await supabase
      .from("clients")
      .update({ food_intolerences: tempIntolerances })
      .eq("user_id", user.id);

    if (error) {
      console.error("[nutrition] Error saving intolerances:", error.message);
      return;
    }

    setIntolerances(tempIntolerances);
    setEditingIntolerances(false);
  };

  return (
    <div className="flex h-screen bg-[#ffffff] text-[#0f172a] overflow-hidden">
      <Sidebar />
      <main className="flex-1 overflow-y-auto p-4 md:p-6 lg:p-8">
        {/* Header - stacks on mobile */}
        <header className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-6">
          <div>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold">Nutrition</h1>
            <p className="text-gray-500 mt-1 text-sm md:text-base">Track your intake and hit your goals</p>
          </div>
          <div className="flex flex-wrap gap-2 md:gap-3 w-full sm:w-auto">
            <button
              onClick={openAddActivity}
              className="flex-1 sm:flex-none px-3 md:px-5 py-2 md:py-3 bg-green-500 text-[#0f172a] text-sm md:text-base font-medium rounded-xl hover:bg-green-400 transition whitespace-nowrap"
            >
              + Activity
            </button>
            <button
              onClick={openLogFood}
              className="flex-1 sm:flex-none px-3 md:px-5 py-2 md:py-3 bg-green-500 text-[#0f172a] text-sm md:text-base font-medium rounded-xl hover:bg-green-400 transition whitespace-nowrap"
            >
              + Food
            </button>
            <button
              onClick={openUpdateGoal}
              className="flex-1 sm:flex-none px-3 md:px-5 py-2 md:py-3 bg-gray-700 text-[#0f172a] text-sm md:text-base font-medium rounded-xl hover:bg-gray-600 transition whitespace-nowrap"
            >
              Update Goal
            </button>
          </div>
        </header>

        {/* Time Range Tabs - scrollable on mobile */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {['12 months', '30 days', '7 days'].map((range) => (
            <button
              key={range}
              onClick={() => setTimeRange(range.toLowerCase().replace(' ', ''))}
              className={`px-3 md:px-4 py-1.5 rounded-lg text-xs md:text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                timeRange === range.toLowerCase().replace(' ', '')
                  ? 'bg-green-500 text-[#0f172a]'
                  : 'bg-[#f1f5f9] text-gray-500 hover:text-[#0f172a]'
              }`}
            >
              {range}
            </button>
          ))}
        </div>

        {/* Loading indicator */}
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 text-green-400 animate-spin" />
          </div>
        )}

        {!isLoading && (
          <>
            {/* Avg Macros Row with Progress Rings */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4 mb-6">
              {[
                { label: 'Calories', value: avgMacros.cal, target: targets.cal, unit: '' },
                { label: 'Protein', value: avgMacros.pro, target: targets.pro, unit: 'g' },
                { label: 'Carbs', value: avgMacros.carb, target: targets.carb, unit: 'g' },
                { label: 'Fat', value: avgMacros.fat, target: targets.fat, unit: 'g' },
              ].map((m) => {
                const percentage = m.value > 0 && m.target > 0 
                  ? Math.min((m.value / m.target) * 100, 100) 
                  : 0;
                
                const getStatus = () => {
                  if (percentage >= 95 && percentage <= 105) return { text: 'On Track', color: 'text-green-400', bgColor: 'bg-green-500/20' };
                  if (percentage > 105) return { text: 'Ahead', color: 'text-blue-400', bgColor: 'bg-blue-500/20' };
                  return { text: 'Behind', color: 'text-orange-400', bgColor: 'bg-orange-500/20' };
                };
                const status = getStatus();

                const radius = 32;
                const circumference = 2 * Math.PI * radius;
                const strokeDashoffset = circumference - (percentage / 100) * circumference;

                return (
                  <div key={m.label} className="bg-[#f8fafc] border border-[#e2e8f0] rounded-xl md:rounded-2xl p-3 md:p-4 hover:border-green-500/30 transition-colors group">
                    <div className="flex items-center justify-between mb-2 md:mb-3">
                      <h3 className="text-[10px] md:text-sm text-gray-500 font-medium">Avg. {m.label}</h3>
                      {m.value > 0 && (
                        <span className={`text-[8px] md:text-xs px-1.5 md:px-2 py-0.5 md:py-1 rounded-full font-medium ${status.bgColor} ${status.color} hidden sm:inline`}>
                          {status.text}
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2 md:gap-3">
                      <div className="relative w-12 h-12 md:w-16 md:h-16 flex-shrink-0">
                        <svg className="transform -rotate-90 w-12 h-12 md:w-16 md:h-16">
                          <circle cx="50%" cy="50%" r="40%" stroke="currentColor" strokeWidth="4" fill="none" className="text-gray-800" />
                          <circle 
                            cx="50%" 
                            cy="50%" 
                            r="40%" 
                            stroke="currentColor" 
                            strokeWidth="4" 
                            fill="none" 
                            strokeDasharray={circumference}
                            strokeDashoffset={strokeDashoffset}
                            className="text-green-400 transition-all duration-500 ease-out"
                            strokeLinecap="round"
                          />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-[10px] md:text-sm font-bold text-[#0f172a]">{Math.round(percentage)}%</span>
                        </div>
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className="text-lg md:text-2xl font-bold text-[#0f172a] leading-tight truncate">
                          {m.value || '-'}{m.unit}
                        </p>
                        <p className="text-[10px] md:text-xs text-gray-500">
                          of {m.target}{m.unit}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Chart + Right Column */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6">
              <div className="lg:col-span-8">
                <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-3 md:p-5 h-full">
                  {chartData.every(d => d.cal === null) ? (
                    <div className="h-full flex items-center justify-center text-gray-500 min-h-[200px] md:min-h-[300px]">
                      No logged data in this period
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height={250} className="md:!h-[300px]">
                      <AreaChart data={chartData}>
                        <CartesianGrid stroke="#475569" vertical={false} />
                        <XAxis 
                          dataKey={timeRange === '12months' ? 'month' : 'day'} 
                          tick={{ fill: '#545f6e', fontSize: 10 }} 
                          interval={timeRange === '30days' ? 4 : 0}
                        />
                        <YAxis tick={{ fill: '#545f6e', fontSize: 10 }} width={40} />
                        <Tooltip contentStyle={{ background: '#f8fafc', border: '1px solid #a78bfa', borderRadius: '8px', fontSize: '12px' }} />
                        <defs>
                          <linearGradient id="fillCal" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#a78bfa" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#a78bfa" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <Area type="monotone" dataKey="cal" stroke="#a78bfa" fillOpacity={1} fill="url(#fillCal)" connectNulls={false} />
                        <Line type="monotone" dataKey="target" stroke="#0f172a" strokeWidth={2} strokeDasharray="5 5" dot={false} connectNulls={true} />
                      </AreaChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>

              <div className="lg:col-span-4 space-y-3 md:space-y-4">
                {/* Quick Stats Card */}
                <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-4 md:p-5">
                  <h2 className="text-base md:text-lg font-semibold mb-3 md:mb-4 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 md:w-5 md:h-5 text-green-400" />
                    Quick Stats
                  </h2>
                  <div className="grid grid-cols-2 gap-2 md:gap-4">
                    <div className="text-center p-2 md:p-3 bg-[#f8fafc] rounded-xl border border-[#e2e8f0]">
                      <div className="flex items-center justify-center mb-1 md:mb-2">
                        <Flame className="w-4 h-4 md:w-5 md:h-5 text-orange-400" />
                      </div>
                      <p className="text-xl md:text-2xl font-bold text-orange-400">{streak}</p>
                      <p className="text-[10px] md:text-xs text-gray-500">Day Streak</p>
                    </div>
                    <div className="text-center p-2 md:p-3 bg-[#f8fafc] rounded-xl border border-[#e2e8f0]">
                      <div className="flex items-center justify-center mb-1 md:mb-2">
                        <Award className="w-4 h-4 md:w-5 md:h-5 text-blue-400" />
                      </div>
                      <p className="text-xl md:text-2xl font-bold text-blue-400">{periodAvg}</p>
                      <p className="text-[10px] md:text-xs text-gray-500">Period Avg</p>
                    </div>
                    <div className="text-center p-2 md:p-3 bg-[#f8fafc] rounded-xl border border-[#e2e8f0] col-span-2">
                      <div className="flex items-center justify-center mb-1 md:mb-2">
                        <Target className="w-4 h-4 md:w-5 md:h-5 text-green-400" />
                      </div>
                      <p className="text-xl md:text-2xl font-bold text-green-400">{consistency}%</p>
                      <p className="text-[10px] md:text-xs text-gray-500">Consistency (Last 30 Days)</p>
                    </div>
                  </div>
                </div>

                {/* Macro Targets */}
                <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-4 md:p-5">
                  <div className="flex justify-between items-center mb-3 md:mb-4">
                    <h2 className="text-base md:text-lg font-semibold">Macro targets</h2>
                    <Edit3 className="w-4 h-4 text-gray-500 cursor-pointer" />
                  </div>
                  <div className="space-y-2 md:space-y-3 text-sm">
                    <div className="flex justify-between"><span className="text-xs md:text-sm text-gray-500">Calorie target</span><span className="font-medium">{targets.cal}</span></div>
                    <div className="flex justify-between"><span className="text-xs md:text-sm text-gray-500">Protein target</span><span className="font-medium">{targets.pro}g</span></div>
                    <div className="flex justify-between"><span className="text-xs md:text-sm text-gray-500">Carb target</span><span className="font-medium">{targets.carb}g</span></div>
                    <div className="flex justify-between"><span className="text-xs md:text-sm text-gray-500">Fat target</span><span className="font-medium">{targets.fat}g</span></div>
                  </div>
                </div>

                {/* Food Preferences */}
                <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-4 md:p-5">
                  <div className="flex justify-between items-center mb-3 md:mb-4">
                    <h2 className="text-base md:text-lg font-semibold">Food Preferences</h2>
                  </div>
                  <div className="space-y-3">
                    {/* Diet Type - stacks input on mobile */}
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                      <span className="text-xs md:text-sm text-gray-500">Diet type</span>
                     {editingDiet ? (
  <div className="flex items-center gap-2">
    <select
      value={tempDiet}
      onChange={(e) => setTempDiet(e.target.value)}
      className="bg-[#f8fafc] border border-[#e2e8f0] rounded px-2 py-1.5 text-sm w-full sm:w-auto appearance-none cursor-pointer focus:border-green-500 outline-none"
      autoFocus
    >
      {[
        "Mediterranean",
        "Ketogenic (Keto)",
        "Paleo",
        "Pescatarian",
        "Vegetarian",
        "Vegan",
        "Intermittent Fasting",
        "Flexible / No Preference"
      ].map((diet) => (
        <option key={diet} value={diet}>{diet}</option>
      ))}
    </select>
    <button onClick={saveDietStyle} className="text-green-400 hover:text-green-300 p-1">
      <Check className="w-4 h-4" />
    </button>
    <button onClick={() => { setTempDiet(dietStyle); setEditingDiet(false); }} className="text-gray-500 hover:text-gray-600 p-1">
      <X className="w-4 h-4" />
    </button>
  </div>
) : (
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm truncate">{dietStyle}</span>
                          <button onClick={() => setEditingDiet(true)} className="text-gray-500 hover:text-[#0f172a] p-1">
                            <Edit3 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                    {/* Dietary Restrictions - stacks input on mobile */}
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                      <span className="text-xs md:text-sm text-gray-500">Dietary restrictions</span>
                      {editingIntolerances ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={tempIntolerances}
                            onChange={(e) => setTempIntolerances(e.target.value)}
                            className="bg-[#f8fafc] border border-[#e2e8f0] rounded px-2 py-1 text-sm w-full sm:w-32 md:w-40"
                            autoFocus
                          />
                          <button onClick={saveIntolerances} className="text-green-400 hover:text-green-300 p-1">
                            <Check className="w-4 h-4" />
                          </button>
                          <button onClick={() => { setTempIntolerances(intolerances); setEditingIntolerances(false); }} className="text-gray-500 hover:text-gray-600 p-1">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm truncate">{intolerances}</span>
                          <button onClick={() => setEditingIntolerances(true)} className="text-gray-500 hover:text-[#0f172a] p-1">
                            <Edit3 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* COLLAPSIBLE MEAL LOGS */}
            <div className="mt-6 space-y-3 md:space-y-4">
              <h2 className="text-xl md:text-2xl font-bold mb-3 md:mb-4">Meal History</h2>
              {(!mealLogs || mealLogs.length === 0) ? (
                <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-6 md:p-8 text-center">
                  <Apple className="w-10 h-10 md:w-12 md:h-12 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-500 mb-3 text-sm md:text-base">No meal logs yet</p>
                  <button
                    onClick={openLogFood}
                    className="text-sm text-green-400 hover:text-green-300 font-medium"
                  >
                    Log your first meal →
                  </button>
                </div>
              ) : (
                mealLogs.map(({ date, logs }) => (
                  <div key={date} className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl overflow-hidden hover:border-green-500/30 transition-colors">
                    <button
                      onClick={() => toggleDay(date)}
                      className="w-full flex items-center justify-between p-4 md:p-5 hover:bg-[#f8fafc] transition-colors"
                    >
                      <div className="flex items-center gap-2 md:gap-3">
                        <Calendar className="w-4 h-4 md:w-5 md:h-5 text-green-400" />
                        <h3 className="text-base md:text-lg font-semibold">{date}</h3>
                        <span className="text-[10px] md:text-xs px-1.5 md:px-2 py-0.5 md:py-1 rounded-full bg-green-500/20 text-green-400">
                          {logs.length} meal{logs.length > 1 ? 's' : ''}
                        </span>
                      </div>
                      {expandedDays[date] ? (
                        <ChevronUp className="w-4 h-4 md:w-5 md:h-5 text-gray-500" />
                      ) : (
                        <ChevronDown className="w-4 h-4 md:w-5 md:h-5 text-gray-500" />
                      )}
                    </button>

                    {expandedDays[date] && (
                      <div className="px-4 md:px-5 pb-4 md:pb-5 space-y-3 md:space-y-4 border-t border-[#e2e8f0]">
                        {logs.map((log) => (
                          <div key={log.id} className="border border-[#e2e8f0] rounded-xl p-3 md:p-4 bg-[#f8fafc] mt-3 md:mt-4">
                            <div className="flex justify-between items-start mb-2 md:mb-3">
                              <div>
                                <p className="text-xs md:text-sm text-green-400 font-medium">
                                  {new Date(log.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                                </p>
                                {log.user_message && (
                                  <p className="text-[10px] md:text-xs text-gray-500 italic mt-1 line-clamp-2">"{log.user_message}"</p>
                                )}
                              </div>
                              <button onClick={() => deleteMealLog(log.id)} className="text-gray-500 hover:text-red-500 transition p-1">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                            {log.meal_plan_text && (
                              <div className="mb-3 md:mb-4 p-2 md:p-3 bg-[#ffffff] rounded-lg border border-[#e2e8f0]">
                                <p className="text-xs md:text-sm text-[#0f172a] whitespace-pre-wrap leading-relaxed">{log.meal_plan_text}</p>
                              </div>
                            )}
                            <div className="space-y-1 md:space-y-2 mb-3 md:mb-4">
                              {log.meals.map((meal: any, i: number) => (
                                <div key={i} className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 text-sm p-2 rounded-lg hover:bg-[#f1f5f9] transition">
                                  <span className="font-medium text-[#0f172a] text-xs md:text-sm">{meal.name}</span>
                                  <div className="flex gap-2 md:gap-3 text-gray-500 text-[10px] md:text-xs">
                                    <span>{meal.calories}kcal</span>
                                    <span>{meal.protein}g P</span>
                                    <span>{meal.carbs}g C</span>
                                    <span>{meal.fat}g F</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div className="border-t border-[#e2e8f0] pt-2 md:pt-3">
                              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1">
                                <span className="text-xs md:text-sm font-semibold text-green-400">Daily Total</span>
                                <div className="flex gap-2 md:gap-4 text-xs md:text-sm">
                                  <span className="font-medium text-[#0f172a]">{log.totals.calories}kcal</span>
                                  <span className="text-gray-500">{log.totals.protein}g P</span>
                                  <span className="text-gray-500">{log.totals.carbs}g C</span>
                                  <span className="text-gray-500">{log.totals.fat}g F</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </>
        )}

        <footer className="mt-8 md:mt-12 text-center text-gray-500 text-xs">
          © {new Date().getFullYear()} Input
        </footer>
      </main>
    </div>
  );
}
